import { queryOptions, type UseQueryOptions } from "@tanstack/react-query";
import type {
  Customer,
  CustomerDetail,
  ServerLockTime,
} from "@shared_types/customer";
import {
  getCustomerTransactionHistory,
  getSettingsDetail,
  getUserDetail,
  getUserServerLock,
  getUserWithPaymentContext,
} from "./users.api";
import type { TransactionSearch } from "@validation/customer";
import type { GetPaymentsHisResponse } from "../documents/documents.types";
import { getPaymentStatus } from "../payment/payment.api";
import type { PaymentStatusResponse } from "../payment/payment.types";

export function getUserDetailOptions<TData = Customer, TError = Error>(
  options?: Omit<
    UseQueryOptions<Customer, TError, TData>,
    "queryKey" | "queryFn"
  >,
) {
  return queryOptions({
    ...options,
    queryKey: ["currentUser"],
    queryFn: () => getUserDetail(),
  });
}

export function getUserPaymentOptions<TData = CustomerDetail, TError = Error>(
  options?: Omit<
    UseQueryOptions<CustomerDetail, TError, TData>,
    "queryKey" | "queryFn"
  >,
) {
  return queryOptions({
    ...options,
    queryKey: ["user-payment"],
    queryFn: () => getUserWithPaymentContext(),
  });
}

export function getUserPaymentHistory<
  TData = GetPaymentsHisResponse,
  TError = Error,
>(
  data: TransactionSearch,
  options?: Omit<
    UseQueryOptions<GetPaymentsHisResponse, TError, TData>,
    "queryKey" | "queryFn"
  >,
) {
  return queryOptions({
    ...options,
    queryKey: ["paymeny-his", data.page, data.limit],
    queryFn: () => getCustomerTransactionHistory(data),
  });
}

export default function getUserSettingsDetailOptions<
  TData = CustomerDetail,
  TError = Error,
>(
  options?: Omit<
    UseQueryOptions<CustomerDetail, TError, TData>,
    "queryKey" | "queryFn"
  >,
) {
  return queryOptions({
    ...options,
    queryKey: ["user-settings"],
    queryFn: () => getSettingsDetail(),
  });
}

export function getSettingsLockTimeOptions<
  TData = ServerLockTime,
  TError = Error,
>(
  options?: Omit<
    UseQueryOptions<ServerLockTime, TError, TData>,
    "queryKey" | "queryFn"
  >,
) {
  return queryOptions({
    ...options,
    queryKey: ["locktime"],
    queryFn: () => getUserServerLock(),
  });
}

export function getUserPaymentStatusOptions<
  TData = PaymentStatusResponse,
  TError = Error,
>(
  idempotencyKey: string | null,
  options?: Omit<
    UseQueryOptions<PaymentStatusResponse, TError, TData>,
    "queryKey" | "queryFn"
  >,
) {
  if (!idempotencyKey) {
    return queryOptions({
      ...options,
      queryKey: ["idempotencyKey", idempotencyKey],
      queryFn: () =>
        Promise.resolve({ status: "UNKNOWN" } as PaymentStatusResponse),
    });
  }
  return queryOptions({
    ...options,
    queryKey: ["idempotencyKey", idempotencyKey],
    queryFn: () => getPaymentStatus(idempotencyKey),
  });
}
